import { cloneDeep } from 'lodash'

import { environment } from '../../../../../environments'
import { standardizePhone } from '../../utils'
import {
  BaseModel,
  ClinicData,
  ClinicEhr,
  ClinicIntegrationSupport,
  ClinicModules, ClinicModulesKeys,
  ClinicModulesPreferences,
  ClinicOptions,
  ClinicPricer,
  ClinicProvider,
  ClinicSocialNetwork,
  IClinicPaymentProvider,
  TaxRate,
} from '../'

export class Clinic extends BaseModel {
  slug: string
  name: string
  email: string
  phoneNumber: string
  languages: string[]
  image: string
  defaultTax?: TaxRate
  twilioNumber: string
  website: string
  paymentProvider: IClinicPaymentProvider
  hasPaymentProvider?: boolean
  stripeCustomerId?: string
  pinCode: string
  type: string
  provider: ClinicProvider
  pricer: ClinicPricer
  socialNetwork: ClinicSocialNetwork
  options: ClinicOptions = {
    inPatientContact: true,
  }
  modules: ClinicModules = {}
  modulesPreferences: ClinicModulesPreferences = {
    estimateTemplates: false,
    unknownInsurance: true,
    paymentPlans: false,
  }
  createAppointmentRequiredFields?: string[]
  createPatientRequiredFields?: string[]
  settings: Record<string, any>
  integration?: ClinicIntegrationSupport
  ehr?: ClinicEhr

  get enabledModules(): string[] {
    const modules: string[] = []

    for (const key of Object.keys(this.modules)) {
      const module = key as ClinicModulesKeys
      if (this.modules[module]) {
        modules.push(module)
      }
    }

    return modules
  }

  constructor(data?: ClinicData) {
    super(data, true)

    this.slug = data?.slug || ''
    this.name = data?.name || ''
    this.email = data?.email || ''
    this.phoneNumber = data?.phoneNumber || ''
    this.languages = data?.languages || ['en']
    this.image = data?.image || ''
    this.defaultTax = data?.defaultTax
    this.twilioNumber = data?.twilioNumber || ''
    this.website = data?.website || ''
    this.paymentProvider = data?.paymentProvider || {}
    this.hasPaymentProvider = data?.hasPaymentProvider || false
    this.stripeCustomerId = data?.stripeCustomerId || ''
    this.pinCode = data?.pinCode || ''
    this.type = data?.type || 'singular'
    this.provider = data?.provider || {}
    this.pricer = data?.pricer || {}
    this.socialNetwork = data?.socialNetwork || {}
    this.options = data?.options || this.options
    this.modules = data?.modules || this.modules
    this.modulesPreferences = data?.modulesPreferences || this.modulesPreferences
    this.createAppointmentRequiredFields = data?.createAppointmentRequiredFields
    this.createPatientRequiredFields = data?.createPatientRequiredFields
    this.settings = data?.settings || {}
    this.integration = data?.integration
    this.ehr = data?.ehr

    // ensure backwards compatibility
    if (!this.provider.type) {
      this.provider.type = 'singular'
    }

    this.options.inPatientContact = true
  }

  integrationFeatureSupported(feature: string): boolean {
    return this.integration?.supports?.includes(feature) ?? false
  }

  integrationFeatureExcluded(feature: string): boolean {
    return this.integration?.excludes?.includes(feature) ?? false
  }

  hasClientLaunchButtons(input: { patient?: any; appointment?: any }) {
    if (
      this.integration &&
      this.integration.clientLaunchButtons &&
      this.integration.clientLaunchButtons.length > 0
    ) {
      return this.integration.clientLaunchButtons.find((button) =>
        this.getClientLaunchButtonEndpoint(button.endpoint, input),
      )
    }

    return false
  }

  getClientLaunchButtonEndpoint(
    endpoint: string,
    input: { patient?: any; appointment?: any },
  ) {
    if (
      this.integrationFeatureSupported('CLIENT_LAUNCH') &&
      typeof this.integration.endpoint === 'string'
    ) {
      const url = endpoint
        .replace('{{ENDPOINT}}', this.integration.endpoint.replace(/\/$/, ''))
        .replace('{{PATIENT_EID}}', input.patient?.eid)
        .replace(
          '{{B64_PATIENT_EID}}',
          input.patient?.eid ? btoa(input.patient?.eid) : '',
        )
        .replace('{{PATIENT_EKEY}}', input.patient?.ekey)
        .replace('{{PATIENT_ID}}', input.patient?.id)
        .replace('{{APPOINTMENT_ID}}', input.appointment?.id)
        .replace('{{APPOINTMENT_EID}}', input.appointment?.eid)
        .replace('{{APPOINTMENT_EKEY}}', input.appointment?.ekey)
        .replace(
          '{{STUDY_UID}}',
          input.appointment?.metadata?.StudyInstanceUID ??
            input.appointment?.metadata?.study_uid,
        )
        .replace(
          '{{METADATA:evo_clinic_id}}',
          input.appointment?.metadata?.evo_clinic_id,
        )

      if (
        !url.includes('{{') &&
        !url.includes('}}') &&
        !url.includes('undefined')
      ) {
        return url
      }
    }

    return ''
  }

  linkToPortal(path = '/') {
    const slug = this.slug
    if (environment.env === 'dev') {
      return `http://localhost:5555${path}`
    } else if (environment.env === 'staging') {
      return `https://${slug}.staging.patientpal.com${path}`
    } else if (environment.env === 'qa') {
      return `https://${slug}.qa.patientpal.com${path}`
    } else if (environment.env === 'demo') {
      return `https://${slug}.demo.patientpal.com${path}`
    } else {
      return `https://${slug}.patientpal.com${path}`
    }
  }

  override toJSON() {
    return {
      id: this.id,
      name: this.name,
      email: this.email,
      slug: this.slug,
      pinCode: this.pinCode,
      type: this.type,
      phoneNumber: standardizePhone(this.phoneNumber),
      languages: this.languages,
      image: this.image,
      twilioNumber: standardizePhone(this.twilioNumber),
      website: this.website,
      provider: cloneDeep(this.provider),
      pricer: cloneDeep(this.pricer),
      socialNetwork: cloneDeep(this.socialNetwork),
      paymentProvider: cloneDeep(this.paymentProvider),
      options: cloneDeep(this.options),
      modules: cloneDeep(this.modules),
      modulesPreferences: cloneDeep(this.modulesPreferences),
      createAppointmentRequiredFields: cloneDeep(this.createAppointmentRequiredFields),
      createPatientRequiredFields: cloneDeep(this.createPatientRequiredFields),
      settings: cloneDeep(this.settings),
      ehr: cloneDeep(this.ehr),
    }
  }

  clone(): Clinic {
    return new Clinic(this.toJSON())
  }
}
